import React, { useState, useEffect, useRef } from "react";
import { Search } from "react-feather";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LoadingSpinner from "../../LoadingSpinner";
import {generateRevenueReport, getReportHeader} from "../../../api/reportApi";
import { FORMS, MODULES } from "../../../const";
import { Grid } from "../job-report/ReportGrid";
import RevenueDistributionSearch from "./RevenueDistributionSearch";

function RevenueDistributionScreen() {
    const auth = useSelector(
        (state) => state.user.permissions[MODULES.Transaction]
    );
    const permissions = auth?.[FORMS.MIS.REPORT.form];

    const [activeAccordion, setActiveAccordion] = useState(1);
    const [searchResults, setSearchResults] = useState([]);
    const [config, setConfig] = useState([]);
    const [isSearchClicked, setSearchClicked] = useState(false);
    const [isSearchResultsPending, setSearchResultsPending] = useState(false);
    const [isHeaderLoading, setIsHeaderLoading] = useState(true);
    const [reportType, setReportType] = useState(1);
    const [dateType, setDateType] = useState(1);
    const [reportName, setReportName] = useState("Vehicle Revenue Distribution Report");
    const gridRef = useRef();

    // Load report headers on report type change
    useEffect(() => {
        const fetchHeaders = async () => {
            setIsHeaderLoading(true);
            try {
                let headerName

                if (reportType === 1) {
                    headerName = "revenue_distribution_by_transporter";
                    setReportName("Transporter Revenue Distribution Report");
                } else {
                    headerName = "revenue_distribution_by_vehicle";
                    setReportName("Vehicle Revenue Distribution Report");
                }


                const response = await getReportHeader(headerName);
                if (
                    response &&
                    response.reportDetails &&
                    response.reportDetails.length > 0
                ) {
                    const dynamicConfig = response.reportDetails.map((detail) => ({
                        field: detail.columnName,
                        headerName: detail.columnAlias,
                        type: detail.dataType,
                    }));
                    setConfig(dynamicConfig);
                } else {
                    throw new Error("No report headers available");
                }
            } catch (error) {
                toast.error("Error loading headers: " + error.message, {
                    autoClose: 5000,
                    containerId: "Main",
                });
            } finally {
                setIsHeaderLoading(false);
            }
        };

        fetchHeaders();
        setSearchResults([]);
        setSearchClicked(false);
    }, [reportType]);

    const handleSearch = async (searchParams) => {
        setSearchClicked(true);
        setSearchResultsPending(true);
        try {
            const response = await generateRevenueReport(searchParams);
            if (response && response.reportData) {
                const mappedResults = mapJobReportsToGridData(response.reportData);
                setSearchResults(mappedResults);
            } else {
                throw new Error("No job reports found.");
            }
        } catch (error) {
            toast.error("Error fetching data: " + error.message, {
                autoClose: 5000,
                containerId: "Main",
            });
        } finally {
            setSearchResultsPending(false);
        }
    };

    const mapJobReportsToGridData = (reports) => {
        return reports.map((report) => {
            const mappedReport = {};
            config.forEach(({ field, type }) => {
                if (report[field] !== undefined) {
                    if (type === "numeric") {
                        // Format as a decimal number
                        mappedReport[field] = new Intl.NumberFormat("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                        }).format(report[field]);
                    } else if (type === "date") {
                        // Assume date is in ISO format and needs to be formatted
                        const date = new Date(report[field]);
                        mappedReport[field] = date.toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                        });
                    } else {
                        mappedReport[field] = report[field];
                    }
                }
            });
            return mappedReport;
        });
    };

    const handleAccordionClick = (index) => {
        setActiveAccordion(index === activeAccordion ? null : index);
    };

    const handleReset = () => {
        setSearchResults([]);
        setSearchClicked(false);
    };

    return (
        <div>
            {/* Accordion for Search */}
            <div
                className="ui styled accordion"
                style={{ width: "100%", marginBottom: "10px" }}
            >
                <div
                    className={`title ${activeAccordion === 1 ? "active" : ""}`}
                    onClick={() => handleAccordionClick(1)}
                >
                    <div>
                        Search
                        <Search size={14} style={{ marginLeft: "10px" }} />
                        <i className="dropdown icon"></i>
                    </div>
                </div>
                <div className={`content ${activeAccordion === 1 ? "active" : ""}`}>
                    <RevenueDistributionSearch
                        searchResHandler={handleSearch}
                        resetHandler={handleReset}
                        reportType={reportType}
                        dateType={dateType}
                        setReportType={setReportType}
                        setDateType={setDateType}
                    />
                    <div className="ui clearing divider"></div>
                </div>
            </div>

            {/* Grid Section */}
            {isSearchClicked && searchResults.length > 0 && (
                <>
                    {isSearchResultsPending || isHeaderLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <Grid
                            heading={reportName}
                            showDownloadButton={true}
                            downloadParam={""}
                            downloadAction="Revenue Distribution Report Download"
                            downloadOnlySelected={false}
                            excelSheetDownload={true}
                            config={config}
                            content={searchResults}
                            ref={gridRef}
                            searchable={true}
                            handleDownloadPopUpChange={() => {
                                const customFileName = `${reportName.replace(/ /g, "_")}_${new Date().toISOString().split("T")[0]}.csv`;
                                gridRef.current.download({ fileName: customFileName });
                            }}
                        />
                    )}
                </>
            )}
        </div>
    );
}

export default RevenueDistributionScreen;
